import { useEffect, useRef } from "react";

/**
 * 6-box OTP input — auto-advance, paste, backspace, arrow navigation.
 * Usage: <OtpBoxInput value={otp} onChange={setOtp} disabled={loading} autoFocus />
 */
export function OtpBoxInput({ value = "", onChange, disabled = false, autoFocus = false }) {
  const inputRefs = useRef([]);
  const digits = (value || "").split("").concat(Array(6).fill("")).slice(0, 6);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    // Focus the first empty box (or the first box if value is empty)
    const firstEmpty = digits.findIndex((d) => !d);
    const idx = firstEmpty === -1 ? 0 : firstEmpty;
    // Defer to next frame so parent transitions/animations don't steal focus
    const id = requestAnimationFrame(() => inputRefs.current[idx]?.focus());
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, disabled]);

  const handleKey = (i, e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = [...digits];
      if (arr[i]) {
        arr[i] = "";
        onChange(arr.join(""));
      } else if (i > 0) {
        arr[i - 1] = "";
        onChange(arr.join(""));
        inputRefs.current[i - 1]?.focus();
      }
      return;
    }
    if (e.key === "ArrowLeft" && i > 0)  { inputRefs.current[i - 1]?.focus(); return; }
    if (e.key === "ArrowRight" && i < 5) { inputRefs.current[i + 1]?.focus(); return; }
  };

  const handleChange = (i, e) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) return;
    // Mobile autofill (iOS QuickType, Android SMS autofill) dumps all 6 digits into
    // whichever cell had focus. Treat any multi-digit input as a full-code paste
    // starting at cell 0, not cell i.
    if (raw.length > 1) {
      const code = raw.slice(0, 6);
      onChange(code);
      const next = Math.min(code.length - 1, 5);
      setTimeout(() => inputRefs.current[next]?.focus(), 0);
      return;
    }
    const arr = [...digits];
    arr[i] = raw;
    onChange(arr.join(""));
    if (i < 5) setTimeout(() => inputRefs.current[i + 1]?.focus(), 0);
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted) {
      onChange(pasted);
      const next = Math.min(pasted.length - 1, 5);
      setTimeout(() => inputRefs.current[next]?.focus(), 0);
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-boxes" role="group" aria-label="One-time code">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={el => inputRefs.current[i] = el}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-label={`Digit ${i + 1} of 6`}
          name={i === 0 ? "otp" : undefined}
          maxLength={6}
          value={d}
          disabled={disabled}
          onChange={e => handleChange(i, e)}
          onKeyDown={e => handleKey(i, e)}
          onPaste={handlePaste}
          onFocus={e => e.target.select()}
          data-testid={`otp-box-${i}`}
          className={[
            "w-11 h-14 text-center text-xl font-bold rounded-xl border-2 transition-all outline-none font-mono",
            d
              ? "border-indigo-500 bg-indigo-50 text-indigo-700"
              : "border-slate-200 bg-white text-slate-900",
            "focus:border-indigo-500 focus:ring-3 focus:ring-indigo-200 focus:bg-indigo-50",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          ].join(" ")}
        />
      ))}
    </div>
  );
}
